import { View } from 'react-native';
import { useState, useEffect } from 'react';
import AuthPage, { AuthHeader, AuthNote, AuthInput as Input, AuthButton as SolidButton } from '@/components/General/AuthPage';
import PasswordChecklist, { getPasswordChecks } from '@/components/General/PasswordChecklist';
import { Keyboard } from 'react-native';
import Toast from 'react-native-toast-message'
import { areStringsPopulated } from '@/utils/InputValidator';
import { supabase } from '@/data/client';
import { useRouter, useLocalSearchParams } from 'expo-router';


const CreateAccountScreen = () => {

    const [accountDetails, setAccountDetails] = useState({});
    const [signingIn, setSigningIn] = useState(false);
    const [invite, setInvite] = useState(null);

    const router = useRouter();
    const { inviteData: encodedInvite } = useLocalSearchParams();

    useEffect(() => {
        if (encodedInvite) {
            try {
                const parsed = JSON.parse(atob(encodedInvite));
                if (new Date(parsed.expiresAt) > new Date()) {
                    setInvite(parsed);
                    setAccountDetails(prev => ({
                        ...prev,
                        email: parsed.email || '',
                        firstName: parsed.firstName || '',
                        lastName: parsed.lastName || ''
                    }));
                }
            } catch {
                // Invalid invite data, ignore
            }
        }
    }, [encodedInvite]);

    const confirmDisabled = !getPasswordChecks(accountDetails.password).every(({met}) => met)
        || accountDetails.password !== accountDetails.confirmPassword;

    return (
        <AuthPage maxWidth={520} showBack footer={
            <AuthNote>Your Reach account is free. Your details are shared only with organizations you choose to join. Each organization manages access to its features.</AuthNote>
        }>
                    <AuthHeader
                        eyebrow={invite ? "You're invited" : 'Get connected'}
                        title={invite ? `Join ${invite.organizationName || 'Your Organization'}` : 'Create your account'}
                        description={invite
                                ? `You've been invited to join ${invite.organizationName}. Create your account below to get started.`
                                : "Start with your details. Once your email is verified, you can find and join your organization."
                            }
                    />

                    <View style={{width:"100%", flexDirection:"row", gap:12}}>
                        <View style={{flex:1, minWidth:0}}><Input
                            title="First Name"
                            autoComplete='given-name'
                            value={accountDetails.firstName}
                            onUpdate={(new_value) => {
                                setAccountDetails({
                                    ...accountDetails,
                                    firstName: new_value.trim()
                                });
                            }}
                        /></View>
                        <View style={{flex:1, minWidth:0}}><Input
                            title="Last Name"
                            autoComplete='family-name'
                            value={accountDetails.lastName}
                            onUpdate={(new_value) => {
                                setAccountDetails({
                                    ...accountDetails,
                                    lastName: new_value.trim()
                                });
                            }}
                        /></View>
                    </View>

                    <View style={{width:"100%"}}>
                        <Input
                            autoCapitalize='none'
                            autoCorrect={false}
                            type="email"
                            title="Email"
                            autoComplete="email"
                            value={accountDetails.email}
                            onUpdate={(new_value) => {
                                setAccountDetails({
                                    ...accountDetails,
                                    email: new_value.trim()
                                });
                            }}
                        />
                    </View>

                    <View style={{width:"100%"}}>
                        <Input
                            title="Password"
                            secureTextEntry={true}
                            autoComplete='new-password'
                            value={accountDetails?.password}
                            onUpdate={(new_value) => {
                                setAccountDetails({
                                    ...accountDetails,
                                    password: new_value
                                });
                            }}
                        />
                    </View>


                    <View style={{width:"100%"}}>
                        <Input
                            title="Confirm Password"
                            autoComplete='new-password'
                            secureTextEntry={true}
                            value={accountDetails?.confirmPassword}
                            onUpdate={(new_value) => {
                                setAccountDetails({
                                    ...accountDetails,
                                    confirmPassword: new_value
                                });
                            }}
                        />
                    </View>

                    <PasswordChecklist password={accountDetails.password} confirmation={accountDetails.confirmPassword} />

                    <View style={{width:"100%"}}>
                        <SolidButton processing={signingIn} disabled={confirmDisabled} onPress={async () => {
                            Keyboard.dismiss();
            
                            if(!areStringsPopulated([accountDetails.firstName, accountDetails.lastName, accountDetails.email])){
                                Toast.show({type: 'error', text2: "Please fill out all fields!"})
                            }
                            else{
                                setSigningIn(true);
                                const redirectUrl = 'https://app.reachchms.com/auth/login/';

                                const { error } = await supabase.auth.signUp(
                                    {
                                        email: accountDetails.email,
                                        password: accountDetails.password,
                                        options: {
                                            emailRedirectTo: redirectUrl,
                                            data: {
                                                firstName: accountDetails.firstName,
                                                lastName: accountDetails.lastName,
                                                inviteData: encodedInvite || null,
                                            }
                                        }
                                    }
                                )

                                setSigningIn(false);

                                if(!error){
                                    Toast.delayShow({type: 'success', text2: invite 
                                        ? `Account created! Check your email to verify and join ${invite.organizationName}.`
                                        : "Successfully created your account. Please check your email for a verification link!"
                                    });
                                    router.replace('/auth/login')
                                }
                                else{
                                    if(error){
                                        if(error.name == "AuthWeakPasswordError"){
                                            if(error.message.includes("Password should contain at least one character of each")){
                                                Toast.show({type: 'error', text2: `That password isn't quite secure enough: You should have at least one uppercase letter, one lowercase letter, one number, and one special character .`})
                                            }
                                            else{
                                                Toast.show({type: 'error', text2: `That password isn't quite secure enough: ${error.message}`})
                                            }
                                        }
                                        else if(error.message == "New password should be different from the old password."){
                                            Toast.show({type: 'error', text2: error.message})
                                        }
                                        else{
                                            Toast.show({type: 'error', text2: "Hmm... Looks like we can't create your account right now. Try again in a bit."})
                                        }
                        
                                        setSigningIn(false);
                                        return false;
                                    }
                                }
                            }
            
                        }} text="Create Account" size="md"/>
                    </View>

        </AuthPage>
    );
}

export default CreateAccountScreen